import { useCallback, useRef, useState } from 'react';
import {
  ShoppingCart,
  Plus,
  Minus,
  CreditCard,
  Banknote,
  Smartphone,
  X,
  DollarSign,
  Search,
  Receipt,
  ChevronDown,
  ScanLine,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { ScannerCodigo } from '@/components/ui/ScannerCodigo';
import { PagamentoPixModal } from './components/PagamentoPixModal';
import { PagamentoDinheiroModal } from './components/PagamentoDinheiroModal';
import { bipeErro, bipeSucesso } from '@/lib/scanner/bipe';
import {
  adicionarItem,
  calcularTotal,
  removerItem,
  removerUnidade,
  unidadesNoCarrinho,
  type ItemCarrinho,
} from './carrinho';

const formasPagamento = [
  { value: 'DINHEIRO', label: 'Dinheiro', icon: Banknote },
  { value: 'PIX', label: 'PIX', icon: Smartphone },
  { value: 'CARTAO_CREDITO', label: 'Crédito', icon: CreditCard },
  { value: 'CARTAO_DEBITO', label: 'Débito', icon: CreditCard },
];

export function PDVPage() {
  const [carrinho, setCarrinho] = useState<ItemCarrinho[]>([]);
  const [formaPagamento, setFormaPagamento] = useState<string>('DINHEIRO');
  const [isAbrirCaixaModalOpen, setIsAbrirCaixaModalOpen] = useState(false);
  const [isFecharCaixaModalOpen, setIsFecharCaixaModalOpen] = useState(false);
  const [painelDireito, setPainelDireito] = useState<'carrinho' | 'resumo'>('carrinho');
  const [formaExpandida, setFormaExpandida] = useState<string | null>(null);
  const [valorAbertura, setValorAbertura] = useState('');
  const [valorFechamento, setValorFechamento] = useState('');
  const [buscaProduto, setBuscaProduto] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [scannerAberto, setScannerAberto] = useState(false);
  const [dinheiroModalAberto, setDinheiroModalAberto] = useState(false);
  const [pixModalAberto, setPixModalAberto] = useState(false);
  const [pixCobranca, setPixCobranca] = useState<{ payload: string; valor: string } | null>(null);
  const [pixErro, setPixErro] = useState<string | null>(null);
  const [avisoLeitura, setAvisoLeitura] = useState<{
    tipo: 'sucesso' | 'erro';
    texto: string;
  } | null>(null);

  const utils = trpc.useUtils();
  const avisoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Espelho síncrono do carrinho. Leituras da câmera chegam mais rápido que o
   * re-render, então ler o estado pelo closure perderia peças quando o caixa
   * escaneia uma pilha em sequência.
   */
  const carrinhoRef = useRef<ItemCarrinho[]>([]);

  const atualizarCarrinho = (proximo: ItemCarrinho[]) => {
    carrinhoRef.current = proximo;
    setCarrinho(proximo);
  };

  const { data: caixaAtual, isLoading: isLoadingCaixa } = trpc.caixas.getCaixaAtual.useQuery();
  // Busca e categoria filtram no servidor, e não sobre a lista já carregada:
  // com o brechó o catálogo passa de 100 itens, e filtrar no cliente diria
  // "nenhum produto encontrado" para uma peça que existe e está em estoque,
  // só porque ela não caiu na primeira página.
  const { data: produtos, isLoading: isLoadingProdutos } = trpc.produtos.list.useQuery({
    page: 1,
    limit: 100,
    ativo: true,
    categoria: categoriaFiltro || undefined,
    search: buscaProduto.trim() || undefined,
  });
  const { data: resumoVendas, isLoading: isLoadingResumo } = trpc.caixas.getResumoVendas.useQuery();
  const { data: categorias } = trpc.produtos.listCategorias.useQuery();

  const abrirCaixaMutation = trpc.caixas.abrir.useMutation({
    onSuccess: () => {
      utils.caixas.getCaixaAtual.invalidate();
      setIsAbrirCaixaModalOpen(false);
      setValorAbertura('');
    },
  });

  const fecharCaixaMutation = trpc.caixas.fechar.useMutation({
    onSuccess: () => {
      utils.caixas.getCaixaAtual.invalidate();
      setIsFecharCaixaModalOpen(false);
      setValorFechamento('');
    },
  });

  const fecharPix = () => {
    setPixModalAberto(false);
    setPixCobranca(null);
    setPixErro(null);
  };

  /**
   * Identifica a cobrança em voo. Fechar o modal, mexer no carrinho e pedir
   * outro QR dispara uma segunda chamada; se a primeira resposta chegar por
   * último, o modal mostraria o QR do carrinho antigo enquanto a venda
   * registraria o atual — exatamente a divergência que o fluxo evita.
   */
  const pixRequisicaoRef = useRef(0);

  const gerarPixMutation = trpc.configuracao.gerarPixCobranca.useMutation({
    onSuccess: (cobranca, _entrada, contexto) => {
      if (contexto !== pixRequisicaoRef.current) return;
      setPixCobranca(cobranca);
      setPixErro(null);
    },
    onError: (e, _entrada, contexto) => {
      if (contexto !== pixRequisicaoRef.current) return;
      setPixCobranca(null);
      setPixErro(e.message);
    },
    onMutate: () => ++pixRequisicaoRef.current,
  });

  const criarVendaMutation = trpc.vendas.create.useMutation({
    onError: (e) => {
      // Nos fluxos de dinheiro e PIX o cliente já pagou quando este erro
      // acontece. Sem mostrá-lo, o caixa fica com dinheiro recebido, venda não
      // registrada e nenhuma explicação na tela. Cada modal expõe o erro por
      // conta própria; aqui só o do PIX precisa do estado separado.
      if (pixModalAberto) setPixErro(e.message);
      avisar('erro', e.message);
    },
    onSuccess: () => {
      atualizarCarrinho([]);
      fecharPix();
      setDinheiroModalAberto(false);
      utils.caixas.getCaixaAtual.invalidate();
      utils.caixas.getResumoVendas.invalidate();
      utils.produtos.list.invalidate();
    },
  });

  const avisar = (tipo: 'sucesso' | 'erro', texto: string) => {
    setAvisoLeitura({ tipo, texto });
    if (avisoTimer.current) clearTimeout(avisoTimer.current);
    avisoTimer.current = setTimeout(() => setAvisoLeitura(null), 2500);
    (tipo === 'sucesso' ? bipeSucesso : bipeErro)();
  };

  const adicionarAoCarrinho = (produto: Parameters<typeof adicionarItem>[1]) => {
    const resultado = adicionarItem(carrinhoRef.current, produto);
    if (!resultado.ok) return resultado.motivo;

    atualizarCarrinho(resultado.carrinho);
    return null;
  };

  /**
   * Leitura da câmera e digitação manual caem aqui pelo mesmo caminho: quem
   * digitou recebe exatamente a mesma validação e o mesmo retorno de quem
   * escaneou.
   */
  const handleLeituraCodigo = useCallback(
    async (codigo: string) => {
      try {
        // staleTime 0 anula o cache global de 5 minutos. Sem isso, reescanear
        // devolveria o estoque antigo — e uma peça já vendida entraria de novo
        // no carrinho para o servidor recusar no fim.
        //
        // `excluirUnidades` só importa quando o código é de produto (digitado
        // à mão): sem ele o servidor devolveria sempre a mesma peça.
        const produto = await utils.produtos.getByCodigo.fetch(
          { codigo, excluirUnidades: unidadesNoCarrinho(carrinhoRef.current) },
          { staleTime: 0 }
        );
        const recusa = adicionarAoCarrinho(produto);

        if (recusa) {
          avisar('erro', recusa);
        } else {
          avisar('sucesso', `${produto.nome} adicionado`);
          // Fecha ao ler: cada etiqueta é uma peça, então a leitura termina
          // aqui em vez de esperar a próxima.
          setScannerAberto(false);
        }
      } catch (e) {
        avisar('erro', e instanceof Error ? e.message : 'Não foi possível ler o código');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [utils]
  );

  const removerDoCarrinho = (produtoId: string) => {
    atualizarCarrinho(removerItem(carrinhoRef.current, produtoId));
  };

  const tirarUmaPeca = (produtoId: string) => {
    atualizarCarrinho(removerUnidade(carrinhoRef.current, produtoId));
  };

  const totalCarrinho = calcularTotal(carrinho);

  const handleAbrirCaixa = () => {
    abrirCaixaMutation.mutate({ valorAbertura });
  };

  const handleFecharCaixa = () => {
    if (caixaAtual) {
      fecharCaixaMutation.mutate({ valorFechamento });
    }
  };

  // Lê do ref, não do state: uma leitura da câmera imediatamente antes do
  // clique em finalizar ainda não teria chegado ao state, e a última peça
  // ficaria de fora da venda.
  const itensDaVenda = () =>
    carrinhoRef.current.map((item) => ({
      produtoId: item.produtoId,
      quantidade: item.unidades.length,
      // As peças escaneadas são exatamente as que saem do estoque.
      unidadeIds: item.unidades.map((u) => u.id),
    }));

  const registrarVenda = () => {
    criarVendaMutation.mutate({
      formaPagamento: formaPagamento as 'DINHEIRO' | 'PIX' | 'CARTAO_CREDITO' | 'CARTAO_DEBITO',
      items: itensDaVenda(),
    });
  };

  /**
   * Dinheiro e cartão registram a venda no clique, como sempre.
   *
   * PIX gera o QR primeiro e só registra depois da confirmação do caixa.
   * A ordem importa: criar a venda antes e estornar depois exigiria um estado
   * pendente em `vendas` e deixaria baixa de estoque para desfazer a cada
   * cliente que desiste. Gerando o QR antes, cancelar não custa nada — o
   * carrinho continua montado e dá para trocar a forma de pagamento.
   */
  const handleFinalizarVenda = () => {
    if (carrinho.length === 0 || !caixaAtual) return;

    // Dinheiro abre a calculadora de troco; cartão não tem troco e continua
    // finalizando em um clique.
    if (formaPagamento === 'DINHEIRO') {
      criarVendaMutation.reset();
      setDinheiroModalAberto(true);
      return;
    }

    if (formaPagamento !== 'PIX') {
      registrarVenda();
      return;
    }

    setPixCobranca(null);
    setPixErro(null);
    setPixModalAberto(true);
    gerarPixMutation.mutate({ items: itensDaVenda() });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatHora = (data: Date | string) => {
    return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(
      new Date(data)
    );
  };

  // O servidor já filtrou por nome; aqui só escondemos o que está sem estoque.
  // A busca por código é local de propósito: `produtos.list` busca por nome, e
  // quem sabe o código escaneia ou digita no leitor.
  const produtosFiltrados = (produtos?.items ?? []).filter((p) => p.quantidadeEstoque > 0);

  if (isLoadingCaixa) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  // Se não há caixa aberto, mostrar tela de abertura
  if (!caixaAtual) {
    return (
      <div className="flex h-96 flex-col items-center justify-center">
        <DollarSign className="mb-4 h-16 w-16 text-gray-400" />
        <h2 className="mb-2 text-xl font-semibold text-gray-700">Nenhum caixa aberto</h2>
        <p className="mb-6 text-gray-500">Abra o caixa para começar a registrar vendas</p>
        <Button onClick={() => setIsAbrirCaixaModalOpen(true)}>Abrir Caixa</Button>

        <Modal
          isOpen={isAbrirCaixaModalOpen}
          onClose={() => setIsAbrirCaixaModalOpen(false)}
          title="Abrir Caixa"
        >
          <div className="space-y-4">
            <Input
              label="Valor de Abertura"
              type="number"
              step="0.01"
              value={valorAbertura}
              onChange={(e) => setValorAbertura(e.target.value)}
              placeholder="0,00"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsAbrirCaixaModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAbrirCaixa} isLoading={abrirCaixaMutation.isPending}>
                Abrir Caixa
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col gap-4">
      {/* Cabeçalho: título + abas + fechar caixa */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900">PDV</h1>
          <div className="flex gap-1 rounded-lg border border-gray-200 p-1">
            <button
              onClick={() => setPainelDireito('carrinho')}
              className={`flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                painelDireito === 'carrinho'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ShoppingCart className="h-4 w-4" />
              Vender
              {carrinho.length > 0 && (
                <span
                  className={`rounded-full px-2 text-xs ${
                    painelDireito === 'carrinho' ? 'bg-white/20' : 'bg-primary-100 text-primary-700'
                  }`}
                >
                  {carrinho.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setPainelDireito('resumo')}
              className={`flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                painelDireito === 'resumo'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Receipt className="h-4 w-4" />
              Resumo
              {resumoVendas && resumoVendas.quantidadeVendas > 0 && (
                <span
                  className={`rounded-full px-2 text-xs ${
                    painelDireito === 'resumo' ? 'bg-white/20' : 'bg-primary-100 text-primary-700'
                  }`}
                >
                  {resumoVendas.quantidadeVendas}
                </span>
              )}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setScannerAberto(true)}>
            <ScanLine className="mr-2 h-4 w-4" />
            Escanear
          </Button>
          <Button variant="outline" onClick={() => setIsFecharCaixaModalOpen(true)}>
            Fechar Caixa
          </Button>
        </div>
      </div>

      {painelDireito === 'carrinho' ? (
        <div className="flex flex-1 flex-col gap-6 overflow-hidden md:flex-row">
          {/* Grid de Produtos */}
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="relative mb-4">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={buscaProduto}
                onChange={(e) => setBuscaProduto(e.target.value)}
                placeholder="Buscar por nome ou código..."
                className="focus:border-primary-500 focus:ring-primary-500 w-full rounded-lg border border-gray-300 py-2 pl-9 pr-9 text-sm focus:outline-none focus:ring-1"
              />
              {buscaProduto && (
                <button
                  type="button"
                  onClick={() => setBuscaProduto('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Limpar busca"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {categorias && categorias.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setCategoriaFiltro('')}
                  className={`rounded-full px-3 py-1 text-sm transition-colors ${
                    categoriaFiltro === ''
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Todas
                </button>
                {categorias.map((categoria) => (
                  <button
                    key={categoria}
                    type="button"
                    onClick={() => setCategoriaFiltro(categoria)}
                    className={`rounded-full px-3 py-1 text-sm transition-colors ${
                      categoriaFiltro === categoria
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {categoria}
                  </button>
                ))}
              </div>
            )}

            <div className="flex-1 overflow-auto">
              {isLoadingProdutos ? (
                <div className="flex items-center justify-center py-8">
                  <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
                </div>
              ) : produtosFiltrados.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Search className="mb-3 h-10 w-10" />
                  <p>
                    {buscaProduto
                      ? `Nenhum produto encontrado para "${buscaProduto}"`
                      : 'Nenhum produto disponível'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {produtosFiltrados.map((produto) => (
                    <Card
                      key={produto.id}
                      className="cursor-pointer transition-shadow hover:shadow-lg"
                      // Tocar no card passa pelo mesmo caminho da câmera: o
                      // servidor escolhe a próxima peça livre daquele produto.
                      onClick={() => void handleLeituraCodigo(produto.codigo)}
                    >
                      <CardContent className="p-4">
                        <h3 className="font-medium text-gray-900">{produto.nome}</h3>
                        <p className="text-primary-600 text-lg font-bold">
                          {formatCurrency(parseFloat(produto.preco))}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="font-mono">{produto.codigo}</span>
                          <span>Estoque: {produto.quantidadeEstoque}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Carrinho */}
          <div className="w-full flex-shrink-0 md:w-96">
            <Card className="flex h-full flex-col overflow-hidden">
              <CardContent className="flex flex-1 flex-col p-0">
                {carrinho.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center text-gray-400">
                    <p>Carrinho vazio</p>
                  </div>
                ) : (
                  <div className="flex-1 overflow-auto p-4">
                    {carrinho.map((item) => (
                      <div
                        key={item.produtoId}
                        className="mb-3 flex items-center justify-between border-b pb-3"
                      >
                        <div className="flex-1">
                          <p className="font-medium">{item.nome}</p>
                          <p className="text-sm text-gray-500">{formatCurrency(item.preco)} cada</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => tirarUmaPeca(item.produtoId)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center font-medium">
                            {item.unidades.length}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleLeituraCodigo(item.codigo)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removerDoCarrinho(item.produtoId)}
                          >
                            <X className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Footer do carrinho */}
                <div className="border-t p-4">
                  <div className="mb-4 flex items-center justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-primary-600">{formatCurrency(totalCarrinho)}</span>
                  </div>

                  {/* Formas de pagamento */}
                  <div className="mb-4 grid grid-cols-2 gap-2">
                    {formasPagamento.map((forma) => {
                      const Icon = forma.icon;
                      return (
                        <button
                          key={forma.value}
                          onClick={() => setFormaPagamento(forma.value)}
                          className={`flex items-center justify-center gap-2 rounded-lg border p-2 text-sm transition-colors ${
                            formaPagamento === forma.value
                              ? 'border-primary-600 bg-primary-50 text-primary-600'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          {forma.label}
                        </button>
                      );
                    })}
                  </div>

                  <Button
                    className="w-full"
                    size="lg"
                    disabled={carrinho.length === 0}
                    isLoading={criarVendaMutation.isPending || gerarPixMutation.isPending}
                    onClick={handleFinalizarVenda}
                  >
                    {formaPagamento === 'PIX'
                      ? 'Gerar QR PIX'
                      : formaPagamento === 'DINHEIRO'
                        ? 'Calcular troco'
                        : 'Finalizar Venda'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        /* Resumo em tela cheia */
        <Card className="flex flex-1 flex-col overflow-hidden">
          {isLoadingResumo ? (
            <div className="flex flex-1 items-center justify-center">
              <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
            </div>
          ) : !resumoVendas || resumoVendas.quantidadeVendas === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center text-gray-400">
              <Receipt className="mb-4 h-16 w-16" />
              <p className="text-lg">Nenhuma venda registrada neste caixa</p>
            </div>
          ) : (
            <div className="flex-1 overflow-auto p-6">
              {/* Cartões de totais */}
              <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <p className="text-sm text-gray-500">Vendas</p>
                  <p className="mt-1 text-3xl font-bold text-gray-900">
                    {resumoVendas.quantidadeVendas}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <p className="text-sm text-gray-500">Total vendido</p>
                  <p className="text-primary-600 mt-1 text-3xl font-bold">
                    {formatCurrency(resumoVendas.totalVendas)}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <p className="text-sm text-gray-500">Ticket médio</p>
                  <p className="mt-1 text-3xl font-bold text-gray-900">
                    {formatCurrency(resumoVendas.ticketMedio)}
                  </p>
                </div>
                <div className="border-primary-200 bg-primary-50 rounded-xl border p-5">
                  <p className="text-primary-700 text-sm">Saldo em caixa</p>
                  <p className="text-primary-700 mt-1 text-3xl font-bold">
                    {formatCurrency(resumoVendas.saldoAtual)}
                  </p>
                  <p className="text-primary-600 mt-1 text-xs">
                    Abertura: {formatCurrency(resumoVendas.valorAbertura)}
                  </p>
                </div>
              </div>

              {/* Por forma de pagamento (clicável) */}
              <h2 className="mb-4 text-lg font-semibold text-gray-900">Por forma de pagamento</h2>
              <p className="mb-3 text-sm text-gray-500">
                Clique em uma forma de pagamento para ver as vendas.
              </p>
              <div className="space-y-3">
                {formasPagamento.map((forma) => {
                  const Icon = forma.icon;
                  const dados = resumoVendas.porFormaPagamento.find(
                    (f) => f.formaPagamento === forma.value
                  );
                  const quantidade = dados?.quantidade ?? 0;
                  const total = dados?.total ?? 0;
                  const aberta = formaExpandida === forma.value;
                  return (
                    <div
                      key={forma.value}
                      className="overflow-hidden rounded-xl border border-gray-200"
                    >
                      <button
                        disabled={quantidade === 0}
                        onClick={() => setFormaExpandida(aberta ? null : forma.value)}
                        className="flex w-full items-center justify-between p-4 text-left transition-colors enabled:hover:bg-gray-50 disabled:cursor-default disabled:opacity-60"
                      >
                        <div className="flex items-center gap-3 text-gray-800">
                          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                            <Icon className="h-5 w-5" />
                          </span>
                          <span className="text-base font-medium">{forma.label}</span>
                          <span className="text-sm text-gray-400">
                            {quantidade} {quantidade === 1 ? 'venda' : 'vendas'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-gray-900">
                            {formatCurrency(total)}
                          </span>
                          {quantidade > 0 && (
                            <ChevronDown
                              className={`h-5 w-5 text-gray-400 transition-transform ${
                                aberta ? 'rotate-180' : ''
                              }`}
                            />
                          )}
                        </div>
                      </button>

                      {aberta && dados && (
                        <div className="grid grid-cols-1 gap-3 border-t bg-gray-50 p-4 sm:grid-cols-2 xl:grid-cols-3">
                          {dados.vendas.map((venda, idx) => (
                            <div
                              key={venda.id}
                              className="rounded-lg border border-gray-200 bg-white p-3"
                            >
                              <div className="mb-2 flex items-center justify-between border-b pb-2 text-sm text-gray-500">
                                <span>
                                  Venda #{dados.vendas.length - idx} · {formatHora(venda.createdAt)}
                                </span>
                                <span className="font-semibold text-gray-900">
                                  {formatCurrency(venda.valorTotal)}
                                </span>
                              </div>
                              <ul className="space-y-1">
                                {venda.itens.map((item, i) => (
                                  <li
                                    key={i}
                                    className="flex justify-between text-sm text-gray-700"
                                  >
                                    <span>
                                      {item.quantidade}x {item.produtoNome}
                                    </span>
                                    <span>{formatCurrency(item.subtotal)}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      )}

      <PagamentoDinheiroModal
        isOpen={dinheiroModalAberto}
        onClose={() => setDinheiroModalAberto(false)}
        total={totalCarrinho}
        confirmando={criarVendaMutation.isPending}
        erro={
          dinheiroModalAberto && criarVendaMutation.error ? criarVendaMutation.error.message : null
        }
        onConfirmar={registrarVenda}
      />

      <PagamentoPixModal
        isOpen={pixModalAberto}
        onClose={fecharPix}
        payload={pixCobranca?.payload ?? null}
        valor={pixCobranca?.valor ?? null}
        erro={pixErro}
        confirmando={criarVendaMutation.isPending}
        onConfirmar={registrarVenda}
      />

      <ScannerCodigo
        isOpen={scannerAberto}
        onClose={() => setScannerAberto(false)}
        onLeitura={handleLeituraCodigo}
        aviso={avisoLeitura}
      />

      {!scannerAberto && avisoLeitura && (
        <div
          role="status"
          className={`fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg px-4 py-2 text-sm font-medium text-white shadow-lg ${
            avisoLeitura.tipo === 'sucesso' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {avisoLeitura.texto}
        </div>
      )}

      {/* Modal Fechar Caixa */}
      <Modal
        isOpen={isFecharCaixaModalOpen}
        onClose={() => setIsFecharCaixaModalOpen(false)}
        title="Fechar Caixa"
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-gray-50 p-4">
            <p className="text-sm text-gray-500">Valor de abertura</p>
            <p className="text-lg font-bold">
              {formatCurrency(parseFloat(caixaAtual?.valorAbertura || '0'))}
            </p>
          </div>
          <Input
            label="Valor de Fechamento"
            type="number"
            step="0.01"
            value={valorFechamento}
            onChange={(e) => setValorFechamento(e.target.value)}
            placeholder="0,00"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsFecharCaixaModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleFecharCaixa} isLoading={fecharCaixaMutation.isPending}>
              Fechar Caixa
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default PDVPage;
